import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

import YouthImg from "../Assets/Youth2.webp";
import WomenImg from "../Assets/WomenAndMothers2.webp";
import SeniorsImg from "../Assets/Seniors2.webp";
import FoodImg from "../Assets/FoodSecurity2.webp";

import CalgaryFoundation from "../Assets/CalgaryFoundation.webp";
import MentalHealthCommission from "../Assets/mentalhealthcommission.webp";
import UnitedWay from "../Assets/UnitedWay.webp";
import CityOfCalgary from "../Assets/CityOfCalgary.webp";

const ease = [0.22, 1, 0.36, 1];

const revealUp = {
  hidden: {
    opacity: 0,
    y: 55,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.85,
      ease,
    },
  },
};

const revealLeft = {
  hidden: {
    opacity: 0,
    x: -55,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.9,
      ease,
    },
  },
};

const revealRight = {
  hidden: {
    opacity: 0,
    x: 55,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.9,
      ease,
    },
  },
};

const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.14,
    },
  },
};

const programs = [
  {
    number: "01",
    title: "Youth",
    eyebrow: "Youth Programs",
    image: YouthImg,
    intro:
      "Creating spaces where young people can build confidence, strengthen their voice, and connect with their community.",
    paragraphs: [
      "EthioCare supports youth through mentorship, education, recreation, and culturally informed mental health conversations.",
      "Our programs give young people opportunities to share their experiences, learn new skills, and develop stronger connections.",
    ],
    highlights: [
      "Youth Mental Health Workshops",
      "Weekly Basketball Program",
      "Annual Summer Camp",
    ],
  },
  {
    number: "02",
    title: "Women & Mothers",
    eyebrow: "Women and Mothers Programs",
    image: WomenImg,
    intro:
      "Supporting women and mothers through connection, practical resources, and culturally relevant programming.",
    paragraphs: [
      "EthioCare is committed to creating supportive spaces where women and mothers can strengthen confidence and build meaningful relationships.",
      "Educational workshops, peer support, and skill-building activities help participants navigate challenges while remaining connected to community.",
    ],
    highlights: [
      "Peer Support",
      "Educational Workshops",
      "Skill Building",
    ],
  },
  {
    number: "03",
    title: "Seniors",
    eyebrow: "Seniors Programs",
    image: SeniorsImg,
    intro:
      "Honouring the knowledge, experiences, and lasting contributions of elders in our community.",
    paragraphs: [
      "Our seniors programs create opportunities for social connection, wellness, and continued participation in community life.",
      "Through gatherings, educational sessions, and recreational activities, seniors can stay active, connected, and engaged.",
    ],
    highlights: [
      "Community Gatherings",
      "Wellness Activities",
      "Educational Sessions",
    ],
  },
  {
    number: "04",
    title: "Food Security",
    eyebrow: "Food Hamper Program",
    image: FoodImg,
    intro:
      "Helping individuals and families access essential food support with care and dignity.",
    paragraphs: [
      "EthioCare's Food Security Hampers Program responds to food insecurity across vulnerable communities.",
      "The program provides essential nutrition and basic food necessities to families experiencing difficult circumstances.",
    ],
    highlights: [
      "Calgary",
      "Brooks",
      "Banff & Canmore",
    ],
  },
];

const partners = [
  {
    name: "Calgary Foundation",
    image: CalgaryFoundation,
    href: "https://calgaryfoundation.org/",
  },
  {
    name: "Mental Health Commission of Canada",
    image: MentalHealthCommission,
    href: "https://www.mentalhealthcommission.ca/English",
  },
  {
    name: "City of Calgary",
    image: CityOfCalgary,
    href: "https://www.calgary.ca/home.html",
  },
  {
    name: "United Way Calgary",
    image: UnitedWay,
    href: "https://calgaryunitedway.org/",
  },
];

const Services = () => {
  return (
    <main className="-mt-10 min-h-screen bg-[#F3FBFA] dark:bg-[#141414] text-[#163C3B] dark:text-white font-sans overflow-hidden transition-colors duration-300">
      {/* HERO */}
      <section className="relative w-full min-h-[80vh] md:min-h-[88vh] overflow-hidden bg-[#163C3B]">
        <motion.img
          initial={{
            scale: 1.08,
            opacity: 0.75,
          }}
          animate={{
            scale: 1,
            opacity: 1,
          }}
          transition={{
            duration: 1.8,
            ease,
          }}
          src={YouthImg}
          alt="EthioCare community programs"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />

        <div className="absolute inset-0 bg-black/35" />

        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/10 to-transparent" />

        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_10%,rgba(0,0,0,0.3)_100%)]" />

        {/* HERO TO PAGE BLEND */}
        <div className="absolute bottom-0 left-0 right-0 z-[2] h-52 md:h-64 bg-gradient-to-b from-transparent via-[#F3FBFA]/35 to-[#F3FBFA] dark:via-[#141414]/40 dark:to-[#141414]" />

        <div className="relative z-10 min-h-[80vh] md:min-h-[88vh] px-6 sm:px-10 lg:px-16 flex items-center justify-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="w-full max-w-6xl mx-auto text-center flex flex-col items-center pb-20 md:pb-28"
          >
            <motion.p
              variants={revealUp}
              className="mb-5 text-sm sm:text-base font-bold uppercase tracking-[0.3em] text-[#E8D34B]"
              style={{
                textShadow:
                  "0 2px 5px rgba(0,0,0,0.8), 0 5px 15px rgba(0,0,0,0.45)",
              }}
            >
              Our Programs
            </motion.p>

            <motion.h1
              variants={revealUp}
              className="max-w-5xl text-white text-5xl sm:text-6xl md:text-7xl lg:text-[5.3rem] font-extrabold leading-[1.02] tracking-[-0.045em]"
              style={{
                textShadow:
                  "0 3px 3px rgba(0,0,0,0.8), 0 7px 16px rgba(0,0,0,0.55), 0 18px 35px rgba(0,0,0,0.3)",
              }}
            >
              Support built around
              <span className="block mt-3 text-[#E5CF3E]">
                community and culture.
              </span>
            </motion.h1>

            <motion.p
              variants={revealUp}
              className="mt-8 max-w-3xl mx-auto text-lg sm:text-xl md:text-2xl text-white leading-relaxed font-semibold"
              style={{
                textShadow:
                  "0 2px 4px rgba(0,0,0,0.9), 0 5px 12px rgba(0,0,0,0.5)",
              }}
            >
              Programs supporting youth, women, mothers, seniors, and families
              throughout our community.
            </motion.p>

            <motion.div variants={revealUp} className="mt-10">
              <a
                href="#programs"
                className="inline-flex min-h-[56px] items-center justify-center bg-[#D7C237] hover:bg-[#E4CF45] text-black px-9 py-4 text-base sm:text-lg font-bold transition-all duration-300 shadow-[0_10px_30px_rgba(0,0,0,0.3)] hover:-translate-y-1"
              >
                Explore Programs
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* INTRO */}
      <section className="relative px-6 sm:px-10 lg:px-16 pb-24 md:pb-32 bg-[#F3FBFA] dark:bg-[#141414]">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{
              once: true,
              amount: 0.25,
            }}
            variants={revealUp}
            className="max-w-5xl"
          >
            <p className="text-xs sm:text-sm uppercase tracking-[0.25em] font-bold text-[#8F8120] dark:text-[#CDB937] mb-6">
              How We Support
            </p>

            <h2
              className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-[#163C3B] dark:text-white leading-[1.08] tracking-[-0.03em]"
              style={{
                textShadow:
                  "0 2px 3px rgba(17,63,61,0.14), 0 7px 18px rgba(17,63,61,0.05)",
              }}
            >
              Different needs.
              <br className="hidden sm:block" /> One connected community.
            </h2>

            <p className="mt-8 text-lg sm:text-xl md:text-2xl text-[#476564] dark:text-gray-300 leading-relaxed max-w-4xl">
              Our programs are shaped around the experiences of the people we
              serve and the communities they call home.
            </p>
          </motion.div>
        </div>
      </section>

      {/* PROGRAMS */}
      <section
        id="programs"
        className="bg-[#EAF6F4] dark:bg-[#191919]"
      >
        {programs.map((program, index) => {
          const imageLeft = index % 2 === 0;

          return (
            <motion.article
              key={program.title}
              initial="hidden"
              whileInView="visible"
              viewport={{
                once: true,
                amount: 0.18,
              }}
              variants={staggerContainer}
              className={`relative overflow-hidden ${
                index !== programs.length - 1
                  ? "border-b border-[#BDD9D5] dark:border-white/5"
                  : ""
              }`}
            >
              <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-2 min-h-[680px]">
                {/* IMAGE LEFT */}
                {imageLeft && (
                  <motion.div
                    variants={revealLeft}
                    className="relative min-h-[420px] lg:min-h-full overflow-hidden"
                  >
                    <img
                      src={program.image}
                      alt={`${program.title} program`}
                      className="absolute inset-0 w-full h-full object-cover"
                    />

                    <div className="absolute inset-0 bg-black/10" />

                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-[#EAF6F4] dark:to-[#191919] hidden lg:block" />

                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#EAF6F4] dark:to-[#191919] lg:hidden" />

                    <p
                      className="absolute top-10 left-8 sm:left-12 text-white text-sm font-bold tracking-[0.2em]"
                      style={{
                        textShadow: "0 2px 8px rgba(0,0,0,0.8)",
                      }}
                    >
                      {program.number}
                    </p>
                  </motion.div>
                )}

                {/* CONTENT */}
                <motion.div
                  variants={imageLeft ? revealRight : revealLeft}
                  className="relative px-6 sm:px-12 lg:px-16 xl:px-24 py-20 lg:py-24 flex flex-col justify-center"
                >
                  <p className="text-xs sm:text-sm uppercase tracking-[0.25em] font-bold text-[#8F8120] dark:text-[#CDB937] mb-5">
                    {program.eyebrow}
                  </p>

                  <h2
                    className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-[#163C3B] dark:text-white leading-[1.05] tracking-[-0.035em]"
                    style={{
                      textShadow:
                        "0 2px 3px rgba(17,63,61,0.14), 0 7px 18px rgba(17,63,61,0.05)",
                    }}
                  >
                    {program.title}
                  </h2>

                  <p className="mt-7 text-xl md:text-2xl text-[#365A58] dark:text-gray-200 leading-relaxed font-medium">
                    {program.intro}
                  </p>

                  <div className="mt-8 space-y-5 text-lg text-[#577371] dark:text-gray-300 leading-relaxed">
                    {program.paragraphs.map((paragraph) => (
                      <p key={paragraph}>{paragraph}</p>
                    ))}
                  </div>

                  <div className="mt-10 border-t border-[#A9CBC6] dark:border-zinc-700">
                    {program.highlights.map((highlight, highlightIndex) => (
                      <div
                        key={highlight}
                        className={`grid grid-cols-[42px_1fr] items-center gap-4 py-4 ${
                          highlightIndex !== program.highlights.length - 1
                            ? "border-b border-[#C6DDDA] dark:border-zinc-800"
                            : ""
                        }`}
                      >
                        <span className="text-xs font-bold text-[#93851F] dark:text-[#CDB937] tracking-[0.14em]">
                          0{highlightIndex + 1}
                        </span>

                        <span className="text-base sm:text-lg font-semibold text-[#365A58] dark:text-gray-200">
                          {highlight}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-10">
                    <Link
                      to="/contact"
                      className="inline-flex min-h-[54px] items-center justify-center bg-[#D1BD3D] hover:bg-[#E0CB4A] text-black px-8 py-4 text-base sm:text-lg font-bold transition-all duration-300 shadow-[0_8px_24px_rgba(32,82,78,0.12)] hover:-translate-y-1"
                    >
                      Get Support
                    </Link>
                  </div>
                </motion.div>

                {/* IMAGE RIGHT */}
                {!imageLeft && (
                  <motion.div
                    variants={revealRight}
                    className="relative min-h-[420px] lg:min-h-full overflow-hidden lg:order-2"
                  >
                    <img
                      src={program.image}
                      alt={`${program.title} program`}
                      className="absolute inset-0 w-full h-full object-cover"
                    />

                    <div className="absolute inset-0 bg-black/10" />

                    <div className="absolute inset-0 bg-gradient-to-l from-transparent via-transparent to-[#EAF6F4] dark:to-[#191919] hidden lg:block" />

                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#EAF6F4] dark:to-[#191919] lg:hidden" />

                    <p
                      className="absolute top-10 right-8 sm:right-12 text-white text-sm font-bold tracking-[0.2em]"
                      style={{
                        textShadow: "0 2px 8px rgba(0,0,0,0.8)",
                      }}
                    >
                      {program.number}
                    </p>
                  </motion.div>
                )}
              </div>
            </motion.article>
          );
        })}
      </section>

      {/* SUPPORT CTA */}
      <section
        id="contact-support"
        className="px-6 sm:px-10 lg:px-16 py-24 md:py-32 bg-[#F3FBFA] dark:bg-[#141414]"
      >
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{
            once: true,
            amount: 0.3,
          }}
          variants={revealUp}
          className="max-w-7xl mx-auto"
        >
          <div className="grid grid-cols-1 lg:grid-cols-[0.8fr_1.2fr] gap-12 lg:gap-24 items-start">
            <div>
              <p className="text-xs sm:text-sm uppercase tracking-[0.25em] font-bold text-[#8F8120] dark:text-[#CDB937] mb-5">
                Finding Support
              </p>

              <h2
                className="text-4xl sm:text-5xl font-extrabold text-[#163C3B] dark:text-white leading-tight tracking-[-0.03em]"
                style={{
                  textShadow:
                    "0 2px 3px rgba(17,63,61,0.13), 0 7px 18px rgba(17,63,61,0.05)",
                }}
              >
                Not sure where to begin?
              </h2>
            </div>

            <div className="border-t border-[#A7CBC6] dark:border-zinc-700 pt-8">
              <p className="text-xl md:text-2xl text-[#365A58] dark:text-gray-300 leading-relaxed">
                EthioCare can help connect individuals and families with the
                program, support, or community resource that best fits their
                needs.
              </p>

              <div className="mt-9 flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="inline-flex min-h-[56px] items-center justify-center bg-[#D1BD3D] hover:bg-[#E0CB4A] text-black px-8 py-4 text-lg font-bold transition-all duration-300 hover:-translate-y-1"
                >
                  Contact EthioCare
                </Link>

                <Link
                  to="/about"
                  className="inline-flex min-h-[56px] items-center justify-center border border-[#759A96] dark:border-white/35 text-[#163C3B] dark:text-white hover:bg-[#163C3B] hover:text-white dark:hover:bg-white dark:hover:text-[#163C3B] px-8 py-4 text-lg font-bold transition-all duration-300 hover:-translate-y-1"
                >
                  About Our Work
                </Link>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* PARTNERS */}
      <section
        id="partners"
        className="px-6 sm:px-10 lg:px-16 py-24 md:py-32 bg-[#EAF6F4] dark:bg-[#191919]"
      >
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{
              once: true,
              amount: 0.3,
            }}
            variants={revealUp}
            className="max-w-4xl mb-14 md:mb-20"
          >
            <p className="text-xs sm:text-sm uppercase tracking-[0.25em] font-bold text-[#8F8120] dark:text-[#CDB937] mb-5">
              Community Collaboration
            </p>

            <h2
              className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-[#163C3B] dark:text-white leading-[1.05] tracking-[-0.035em]"
              style={{
                textShadow:
                  "0 2px 3px rgba(17,63,61,0.14), 0 7px 18px rgba(17,63,61,0.05)",
              }}
            >
              Our partners and supporters.
            </h2>

            <p className="mt-7 text-lg sm:text-xl text-[#577371] dark:text-gray-300 leading-relaxed max-w-3xl">
              Our work is strengthened by organizations committed to community
              wellbeing and culturally responsive care.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{
              once: true,
              amount: 0.15,
            }}
            variants={staggerContainer}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-[#B6D3CF] dark:border-zinc-700"
          >
            {partners.map((partner, index) => (
              <motion.a
                key={partner.name}
                variants={revealUp}
                href={partner.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Visit ${partner.name}`}
                className="group relative min-h-[245px] p-8 border-r border-b border-[#B6D3CF] dark:border-zinc-700 flex flex-col justify-between transition-colors duration-300 hover:bg-[#F3FBFA] dark:hover:bg-[#202020]"
              >
                <span className="text-xs tracking-[0.16em] font-bold text-[#8F8120] dark:text-[#CDB937]">
                  0{index + 1}
                </span>

                <img
                  src={partner.image}
                  alt={partner.name}
                  className="my-8 h-20 w-full object-contain transition-transform duration-500 group-hover:scale-105"
                />

                <p className="text-sm uppercase tracking-[0.13em] font-bold text-[#577371] dark:text-gray-300 transition-transform duration-300 group-hover:translate-x-1">
                  Visit Website
                </p>
              </motion.a>
            ))}
          </motion.div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-6 sm:px-10 lg:px-16 py-24 md:py-32 bg-[#163C3B] dark:bg-[#101010]">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{
            once: true,
            amount: 0.3,
          }}
          variants={revealUp}
          className="max-w-7xl mx-auto"
        >
          <div className="grid lg:grid-cols-[1fr_auto] gap-12 lg:gap-20 items-end">
            <div className="max-w-4xl">
              <p className="text-xs sm:text-sm uppercase tracking-[0.25em] font-bold text-[#D8C342] mb-6">
                Connect With Us
              </p>

              <h2
                className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white leading-[1.08] tracking-[-0.035em]"
                style={{
                  textShadow:
                    "0 3px 5px rgba(0,0,0,0.4), 0 10px 25px rgba(0,0,0,0.2)",
                }}
              >
                Find the support that feels right for you.
              </h2>

              <p className="mt-7 text-lg sm:text-xl text-white/75 leading-relaxed max-w-3xl">
                Connect with EthioCare and learn more about the programs
                available in your community.
              </p>
            </div>

            <Link
              to="/contact"
              className="inline-flex min-h-[56px] items-center justify-center whitespace-nowrap bg-[#D1BD3D] hover:bg-[#E0CB4A] text-black px-8 py-4 text-lg font-bold transition-all duration-300 hover:-translate-y-1"
            >
              Contact EthioCare
            </Link>
          </div>
        </motion.div>
      </section>
    </main>
  );
};

export default Services;